// Per-hex grid fields: decode every *.grid.bin with core/gridfile.ts and merge their field tables.
// A field whose file is missing is filled with zeros so the world layer never branches on it.

import { HEX_COUNT } from '../core/grid.ts';
import { readGridFile, type FieldArray, type FieldTypeName } from '../core/gridfile.ts';
import { GRID_FIELDS } from '../core/schema.ts';
import type { Reader } from './fetch.ts';

/** Grid files in load order (ARCHITECTURE §3.2). */
export const GRID_FILES = ['physical.grid.bin', 'human.grid.bin', 'sites.grid.bin'] as const;

const ZERO: Record<FieldTypeName, () => FieldArray> = {
  u8: () => new Uint8Array(HEX_COUNT),
  i8: () => new Int8Array(HEX_COUNT),
  u16: () => new Uint16Array(HEX_COUNT),
  i16: () => new Int16Array(HEX_COUNT),
  u32: () => new Uint32Array(HEX_COUNT),
  f32: () => new Float32Array(HEX_COUNT),
};

/** Merge decoded files; a name seen twice keeps the first and records a warning. */
export function mergeGrids(files: Array<Map<string, FieldArray>>, warnings: string[]): Map<string, FieldArray> {
  const out = new Map<string, FieldArray>();
  for (const fields of files) {
    for (const [name, arr] of fields) {
      if (out.has(name)) {
        warnings.push(`data: grid field ${name} appears in two files; keeping the first`);
        continue;
      }
      out.set(name, arr);
    }
  }
  return out;
}

/** Add a zero array for every schema field no file provided. */
export function fillMissing(grid: Map<string, FieldArray>, warnings: string[]): void {
  for (const [name, type] of Object.entries(GRID_FIELDS)) {
    if (grid.has(name)) {
      continue;
    }
    warnings.push(`data: grid field ${name} missing; using zeros`);
    grid.set(name, ZERO[type]());
  }
}

async function readOne(reader: Reader, path: string): Promise<Map<string, FieldArray>> {
  const buf = await reader.buffer(path);
  if (!buf) {
    return new Map();
  }
  try {
    return readGridFile(buf);
  } catch (err) {
    reader.warnings.push(`data: ${path} unreadable (${String(err)})`);
    return new Map();
  }
}

export async function loadGrid(reader: Reader): Promise<Map<string, FieldArray>> {
  const files = await Promise.all(GRID_FILES.map((f) => readOne(reader, f)));
  const grid = mergeGrids(files, reader.warnings);
  fillMissing(grid, reader.warnings);
  return grid;
}

// Typed accessors. loadGrid guarantees presence; a wrong type means the bake and schema disagree.

function typed<T extends FieldArray>(grid: Map<string, FieldArray>, name: string, ctor: { new (n: number): T }): T {
  const arr = grid.get(name);
  if (arr instanceof ctor) {
    return arr;
  }
  throw new Error(`grid field ${name}: expected ${ctor.name}`);
}

export function u8Field(grid: Map<string, FieldArray>, name: string): Uint8Array {
  return typed(grid, name, Uint8Array);
}

export function u16Field(grid: Map<string, FieldArray>, name: string): Uint16Array {
  return typed(grid, name, Uint16Array);
}

export function i16Field(grid: Map<string, FieldArray>, name: string): Int16Array {
  return typed(grid, name, Int16Array);
}

export function f32Field(grid: Map<string, FieldArray>, name: string): Float32Array {
  return typed(grid, name, Float32Array);
}
